import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Item, ItemActions, ItemContent, ItemTitle } from '../ui/item';
import { RecordListItem } from './RecordListItem';

export type CareTeamMember = {
  id: string;
  firstName: string;
  lastName: string;
};

/** An external care-team member linked to the patient, flattened for display. */
export type ExternalCareTeamMemberDisplay = {
  roleId: string;
  name: string;
  practiceName: string;
  /** SNOMED specialty display, shown first in the muted description. */
  specialty?: string;
  /** SNOMED practitioner type display, shown last in the muted description. */
  providerType?: string;
};

export type CareTeamSectionsProps = {
  /** Practitioners who are members of the patient's organization. */
  internal: CareTeamMember[];
  /** External members currently linked to the patient's care team. */
  external?: ExternalCareTeamMemberDisplay[];
  /** Renders the "Add external provider" button when provided. */
  onAddExternalProvider?: () => void;
  /** Renders an unlink action on each external member when provided. */
  onUnlinkExternal?: (member: ExternalCareTeamMemberDisplay) => void;
};

const formatName = (member: CareTeamMember): string => `${member.firstName} ${member.lastName}`.trim();

const formatExternalDescription = (member: ExternalCareTeamMemberDisplay): string =>
  [member.specialty, member.practiceName, member.providerType].filter(Boolean).join(' · ');

/**
 * Directory of the patient's care team, split into an `Internal` section (practitioners
 * in the patient's organization) and an `External` section (linked external members, with
 * optional add/unlink actions).
 */
export const CareTeamSections = ({
  internal,
  external = [],
  onAddExternalProvider,
  onUnlinkExternal,
}: CareTeamSectionsProps): React.ReactElement => {
  const { t } = useTranslation('records');

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6" data-testid="care-team-sections">
      <section aria-label={t('careTeamInternal')}>
        <h3 className="mb-2 border border-transparent px-4 text-sm font-semibold text-muted-foreground">
          {t('careTeamInternal')}
        </h3>
        <div className="space-y-2" data-testid="care-team-internal-list">
          {internal.length > 0 ? (
            internal.map((member) => <RecordListItem key={member.id} title={formatName(member)} />)
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('noCareTeamMembers')}</p>
          )}
        </div>
      </section>

      <section aria-label={t('careTeamExternal')}>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="border border-transparent px-4 text-sm font-semibold text-muted-foreground">
            {t('careTeamExternal')}
          </h3>
          {onAddExternalProvider && (
            <Button
              variant="outline"
              size="sm"
              data-testid="add-external-care-team-member-button"
              onClick={onAddExternalProvider}
            >
              <PlusIcon className="size-4" />
              {t('addExternalProvider')}
            </Button>
          )}
        </div>
        <div className="space-y-2" data-testid="care-team-external-list">
          {external.length > 0 ? (
            external.map((member) => (
              <Item key={member.roleId} size="sm" data-testid={`care-team-external-member-${member.roleId}`}>
                <ItemContent>
                  <ItemTitle className="flex items-center gap-2 text-sm">{member.name}</ItemTitle>
                  {formatExternalDescription(member) && (
                    <span className="text-xs text-muted-foreground">{formatExternalDescription(member)}</span>
                  )}
                </ItemContent>
                {onUnlinkExternal && (
                  <ItemActions>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`unlink-external-member-${member.roleId}`}
                      aria-label={`${t('unlinkExternalProvider')} ${member.name}`}
                      onClick={() => onUnlinkExternal(member)}
                    >
                      {t('unlinkExternalProvider')}
                    </Button>
                  </ItemActions>
                )}
              </Item>
            ))
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="care-team-external-empty">
              {t('noExternalCareTeamMembers')}
            </p>
          )}
        </div>
      </section>
    </div>
  );
};
